import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";
import { StepTitle, ErrorState, Skeleton, PrimaryButton } from "@/components/BookingShell";
import { formatPrice, formatDuration } from "@/lib/format";
import { groupServices } from "@/lib/serviceGroups";
import { BookingSuggestions } from "@/components/BookingSuggestions";

export function ServiceStep({ setFooter }) {
    const { category, service, setService, next, cart, setCart, goTo, setDate, setTime } = useBooking();
    const [services, setServices] = useState(null);
    const [rules, setRules] = useState({});
    const [expanded, setExpanded] = useState(null);
    const [multi, setMulti] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        setError(null);
        setServices(null);
        api.catalogue().then((d) => { if (active) {
            setRules(d.rules);
            setMulti(d.multi_checkout_enabled);
            if (!d.multi_checkout_enabled) {
                setCart([]);
                setService((previous) => previous?.categoryId === category?.id ? previous : null);
            }
            setServices(d.services.filter((s) => s.categoryId === category?.id && (d.multi_checkout_enabled || (d.rules[String(s.id)]?.type || "main") === "main")));
        } }).catch((e) => { if (active) setError(e); });
        return () => { active = false; };
    }, [category?.id, setCart, setService]);

    const selected = service;
    const continueBooking = useCallback(async () => {
        if (!multi) { next(); return; }
        if (cart.length === 1 && cart[0].quantity === 1 && (rules[String(cart[0].service.id)]?.type || "main") === "main") {
            setService(cart[0].service);
            next();
            return;
        }
        setBusy(true); setError(null);
        try {
            const quote = await api.cartQuote(cart.map((c) => ({service_id:c.service.id, quantity:c.quantity})));
            setService({ id: cart[0].service.id, name: quote.items.map((i) => `${i.name}${i.quantity > 1 ? ` × ${i.quantity}` : ""}`).join(" + "), price:quote.price, duration:quote.duration, deposit:quote.amount_due, depositPayment:"fixed", cartQuote:quote });
            next();
        } catch (e) { setError(e); } finally { setBusy(false); }
    }, [multi, next, cart, setService, rules]);
    const select = (s) => {
        if (!multi) { setService(s); setDate(null); setTime(null); return; }
        let items = cart.some((c) => c.service.id === s.id) ? cart.filter((c) => c.service.id !== s.id) : [...cart, {service:s, quantity:rules[String(s.id)]?.quantity_min || 1}];
        const ids = items.map((c) => c.service.id);
        items = items.filter((c) => !rules[String(c.service.id)]?.requires?.length || rules[String(c.service.id)].requires.some((id) => ids.includes(id)));
        setCart(items);
    };

    useEffect(() => {
        setFooter(
            (multi ? cart.length > 0 : selected) ? (
                <PrimaryButton onClick={continueBooking} disabled={busy} testId="service-continue-button">
                    {busy ? "Checking treatments…" : "Continue"}
                </PrimaryButton>
            ) : null
        );
        return () => setFooter(null);
    }, [selected, multi, cart.length, continueBooking, busy, setFooter]);

    const groups = useMemo(() => groupServices(services || [], rules), [services, rules]);

    return (
        <div>
            <StepTitle
                eyebrow={category?.name || "Treatment"}
                title={<>Select your <em className="italic gold-text not-italic font-serif">treatment</em></>}
                subtitle="All treatments include a private consultation with Angela."
                testId="service-step-title"
            />

            {error && <ErrorState error={error} onRetry={() => window.location.reload()} />}
            {multi && cart.length > 0 && <div className="rounded-2xl border border-gold/50 p-4 mb-4 text-white">
                <p className="font-semibold">Your treatments</p>
                {cart.map((c) => <div key={c.service.id} className="flex items-center justify-between gap-3 py-2 text-sm"><span>{c.service.name}{rules[String(c.service.id)]?.option_label ? ` · ${rules[String(c.service.id)].option_label}` : ""} × {c.quantity}</span><button className="min-h-11 text-gold" onClick={() => select(c.service)}>Remove</button></div>)}
                <button className="min-h-11 text-gold underline" onClick={() => goTo("category")}>Add another treatment</button>
            </div>}

            <BookingSuggestions />

            {!services && !error && (
                <div className="space-y-3">
                    {[1, 2, 3].map((k) => (
                        <Skeleton key={k} className="h-24" />
                    ))}
                </div>
            )}

            {services && groups.length === 0 && (
                <div className="rounded-2xl glass p-8 text-center" data-testid="service-empty-state">
                    <div className="font-serif text-2xl">No treatments in this category</div>
                </div>
            )}

            {groups.length > 0 && (
                <ul className="space-y-3" data-testid="service-list">
                    {groups.map((g) => <React.Fragment key={g.parent.id}>
                    {g.hasVariants && <li><button type="button" aria-expanded={expanded === g.parent.id} onClick={() => setExpanded(expanded === g.parent.id ? null : g.parent.id)} className="w-full text-left rounded-2xl border hairline p-5 text-white">
                <span className="font-serif text-xl">{g.parent.name}</span>
                <span className="block text-sm text-gold mt-2">From {formatPrice(g.fromPrice)} · {g.options.length} options {expanded === g.parent.id ? "▴" : "▾"}</span>
                    </button></li>}
                    {(!g.hasVariants || expanded === g.parent.id ? g.options : []).map((s) => {
                        const active = multi ? cart.some((c) => c.service.id === s.id) : selected?.id === s.id;
                        const r = rules[String(s.id)] || {};
                        const unavailable = multi && !active && ((r.requires?.length && !cart.some((c) => r.requires.includes(c.service.id))) || cart.some((c) => r.incompatible?.includes(c.service.id) || rules[String(c.service.id)]?.incompatible?.includes(s.id)));
                        return (
                            <li key={s.id}>
                                <button
                                    onClick={() => select(s)} disabled={unavailable}
                                    data-testid={`service-card-${s.id}`}
                                    className={`w-full text-left rounded-2xl border p-5 transition-all tap-scale
                                        ${active
                                            ? "bg-navy-500/60 border-gold shadow-[0_0_0_1px_rgba(197,160,89,0.4),0_20px_40px_-20px_rgba(197,160,89,0.45)]"
                                            : "bg-navy-500/25 hairline hover:border-gold/50"}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-serif text-xl leading-snug text-white">{rules[String(s.id)]?.option_label || s.name}</div>
                                            {unavailable && <p className="text-xs text-gold mt-2">Select an eligible main treatment and remove incompatible treatments first.</p>}
                                            {s.description && (
                                                <p className="mt-1.5 text-sm text-white/55 leading-relaxed line-clamp-2">
                                                    {s.description.replace(/<[^>]*>/g, "")}
                                                </p>
                                            )}
                                            <div className="mt-3 flex items-center gap-4 text-xs text-white/60">
                                                {s.duration ? (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Clock className="h-3.5 w-3.5 text-gold/70" strokeWidth={1.5} />
                                                        {formatDuration(s.duration)}
                                                    </span>
                                                ) : null}
                                                <span className="text-white/85 text-sm font-medium">
                                                    {formatPrice(s.price)}
                                                </span>
                                            </div>
                                        </div>
                                        <div
                                            className={`shrink-0 h-6 w-6 rounded-full border flex items-center justify-center transition-all
                                                ${active ? "bg-gold border-gold" : "border-white/25"}`}
                                        >
                                            {active && <Check className="h-3.5 w-3.5 text-navy" strokeWidth={3} />}
                                        </div>
                                    </div>
                                </button>
                                {multi && active && r.quantity_enabled && <label className="flex items-center justify-between gap-3 py-3 text-white">Quantity<input aria-label={`Quantity for ${s.name}`} type="number" min={r.quantity_min} max={r.quantity_max} step="1" value={cart.find((c) => c.service.id === s.id)?.quantity || 1} onChange={(e) => setCart(cart.map((c) => c.service.id === s.id ? {...c,quantity:Number(e.target.value)} : c))} className="w-20 min-h-11 rounded border border-gold bg-navy px-3" /></label>}
                            </li>
                        );
                    })}</React.Fragment>)}
                </ul>
            )}
        </div>
    );
}
